import React, { useEffect, useMemo, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import "../assets/css/Issues.css";
import "../assets/css/Projects.css";
import "../assets/css/Permissions.css";
import "../assets/css/ViewCustomer.css";
import { fetchSubTasksByTaskId } from "./subTaskService";
import { fetchAllTasks } from "./taskService";
import { fetchAllUsers } from "./issues/issueService";
import { fetchTestCaseById, updateTestCase } from "./issues/testCaseService";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import { getAuthItem } from "../utils/authStorage";

const STATUS_OPTIONS = ["Not Executed", "Passed", "Failed", "Blocked"];
const VALID_STATUSES = new Set(STATUS_OPTIONS);
const PRIORITY_OPTIONS = ["High", "Medium", "Low"];

function normalizeStatus(s) {
  if (!s || typeof s !== "string") return "Not Executed";
  const trimmed = s.trim();
  if (VALID_STATUSES.has(trimmed)) return trimmed;
  const lower = trimmed.toLowerCase();
  if (lower === "not run") return "Not Executed";
  const match = STATUS_OPTIONS.find((opt) => opt.toLowerCase() === lower);
  return match || "Not Executed";
}

function EditTestCase() {
  const navigate = useNavigate();
  const { testCaseId } = useParams();
  const role = useMemo(() => {
    try {
      const u = getAuthItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);
  const canEdit = canPerformActionOnPage(PAGES.ISSUES, ACTIONS.EDIT, role);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [subtasks, setSubtasks] = useState([]);
  const [loadingSubtasks, setLoadingSubtasks] = useState(false);

  const [form, setForm] = useState({
    taskId: "",
    subTaskId: "",
    title: "",
    description: "",
    preConditions: "",
    steps: "",
    expected: "",
    actual: "",
    status: "Failed",
    priority: "Medium",
    comments: "",
    executedBy: ""
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchTestCaseById(testCaseId);
        if (!data) {
          setError("Test case not found.");
          setForm((p) => ({ ...p, taskId: "", subTaskId: "", title: "" }));
        } else {
          const taskId = data.Task_Id ?? data.task_id ?? "";
          const subTaskId = data.Sub_Task_Id ?? data.sub_task_id ?? "";
          setForm({
            taskId: taskId ? String(taskId) : "",
            subTaskId: subTaskId ? String(subTaskId) : "",
            title: data.Title ?? data.title ?? "",
            description: data.Description ?? data.description ?? "",
            preConditions: data.Pre_Conditions ?? data.pre_conditions ?? "",
            steps: data.Steps ?? data.steps ?? "",
            expected: data.Expected_Result ?? data.expected_result ?? "",
            actual: data.Actual_Result ?? data.actual_result ?? "",
            status: normalizeStatus(data.Status ?? data.status),
            priority: data.Priority ?? data.priority ?? "Medium",
            comments: data.Comments ?? data.comments ?? "",
            executedBy: data.Executed_By != null ? String(data.Executed_By) : ""
          });
        }
      } catch (err) {
        console.error("Error fetching test case:", err);
        setError(err.response?.data?.message || err.response?.data?.error || "Failed to load test case.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [testCaseId]);

  useEffect(() => {
    const loadTasks = async () => {
      setLoadingTasks(true);
      try {
        const list = await fetchAllTasks();
        const mapped = (list || [])
          .map((t) => ({
            id: t.Task_Id ?? t.task_id ?? t.id,
            title: t.Title ?? t.title ?? t.Task_Title ?? t.task_title ?? "—"
          }))
          .filter((x) => x.id != null);
        mapped.sort((a, b) => String(a.title).localeCompare(String(b.title)));
        setTasks(mapped);
      } catch (err) {
        console.error("Error loading tasks:", err);
        setTasks([]);
      } finally {
        setLoadingTasks(false);
      }
    };
    loadTasks();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const list = await fetchAllUsers();
        const sorted = (list || []).sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setUsers(sorted);
      } catch (err) {
        console.error("Error loading users:", err);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    const taskIdNum = Number(form.taskId);
    if (!taskIdNum || !Number.isFinite(taskIdNum)) {
      setSubtasks([]);
      setForm((p) => ({ ...p, subTaskId: "" }));
      return;
    }
    setLoadingSubtasks(true);
    fetchSubTasksByTaskId(taskIdNum)
      .then((list) => {
        const mapped = (list || [])
          .map((st) => ({
            id: st.Sub_Task_Id ?? st.sub_task_id ?? st.id,
            title: st.Title ?? st.title ?? "—"
          }))
          .filter((x) => x.id != null);
        setSubtasks(mapped);
      })
      .catch(() => setSubtasks([]))
      .finally(() => setLoadingSubtasks(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.taskId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => {
      const next = { ...p, [name]: value };
      if (name === "taskId") next.subTaskId = "";
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;

    const taskId = Number(form.taskId);
    const subTaskId = form.subTaskId ? Number(form.subTaskId) : null;
    const title = (form.title || "").trim();
    if (!taskId || !title) {
      setError("Task and Title are required.");
      return;
    }

    const payload = {
      Task_Id: taskId,
      Sub_Task_Id: subTaskId,
      Title: title,
      Description: (form.description || "").trim() || null,
      Pre_Conditions: (form.preConditions || "").trim() || null,
      Steps: (form.steps || "").trim() || null,
      Expected_Result: (form.expected || "").trim() || null,
      Actual_Result: (form.actual || "").trim() || null,
      Status: normalizeStatus(form.status),
      Priority: form.priority || "Medium",
      Comments: (form.comments || "").trim() || null,
      Executed_By: form.executedBy ? Number(form.executedBy) : null
    };
    if (!payload.Sub_Task_Id) delete payload.Sub_Task_Id;
    if (payload.Executed_By == null) delete payload.Executed_By;

    setSubmitting(true);
    setError("");
    try {
      await updateTestCase(testCaseId, payload);
      alert("Test case updated successfully.");
      navigate("/issues/test-cases");
    } catch (err) {
      console.error("Error updating test case:", err);
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to update test case.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="issues-container">
        <div className="viewlead-header" style={{ marginBottom: 12 }}>
          <button type="button" className="viewlead-back-link" onClick={() => navigate("/issues/test-cases")}>
            <FaArrowLeft /> Back 
          </button>
        </div>
        <div className="issues-main-card">
          <div style={{ padding: 18, color: "#5E6C84" }}>Loading test case...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="issues-container">
      <div className="viewlead-header" style={{ marginBottom: 12 }}>
        <button type="button" className="viewlead-back-link" onClick={() => navigate("/issues/test-cases")}>
          <FaArrowLeft /> Back 
        </button>
      </div>

      <div className="issues-main-card">
        <form className="issues-form" onSubmit={handleSubmit}>
          <div className="issues-form-title">Edit Test Case</div>

          {!canEdit ? (
            <div className="issues-alert">You don't have permission to edit test cases.</div>
          ) : null}
          {error ? <div className="issues-alert issues-alert-error">{error}</div> : null}

          <div className="issues-form-grid">
            <div className="form-group">
              <label>Task <span className="required">*</span></label>
              <select
                name="taskId"
                value={form.taskId}
                onChange={handleChange}
                required
                disabled={!canEdit || loadingTasks}
              >
                <option value="">
                  {loadingTasks ? "Loading tasks..." : "Select Task"}
                </option>
                {tasks.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Sub Task</label>
              <select
                name="subTaskId"
                value={form.subTaskId}
                onChange={handleChange}
                disabled={!canEdit || !form.taskId || loadingSubtasks}
              >
                <option value="">
                  {loadingSubtasks
                    ? "Loading subtasks..."
                    : subtasks.length
                      ? "No sub task"
                      : "No sub task (select task)"}
                </option>
                {subtasks.map((st) => (
                  <option key={st.id} value={String(st.id)}>
                    {st.title} (#{st.id})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Title <span className="required">*</span></label>
              <input type="text" name="title" value={form.title} onChange={handleChange} required disabled={!canEdit} />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={form.status} onChange={handleChange} disabled={!canEdit}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Priority</label>
              <select name="priority" value={form.priority} onChange={handleChange} disabled={!canEdit}>
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Executed By</label>
              <select
                name="executedBy"
                value={form.executedBy}
                onChange={handleChange}
                disabled={!canEdit || loadingUsers}
              >
                <option value="">
                  {loadingUsers ? "Loading users..." : "Select user"}
                </option>
                {users.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Pre Conditions</label>
              <input type="text" name="preConditions" value={form.preConditions} onChange={handleChange} disabled={!canEdit} />
            </div>

            <div className="form-group issues-description-full">
              <label>Description</label>
              <textarea name="description" value={form.description} onChange={handleChange} rows={3} disabled={!canEdit} />
            </div>
            <div className="form-group issues-description-full">
              <label>Steps</label>
              <textarea name="steps" value={form.steps} onChange={handleChange} rows={3} disabled={!canEdit} />
            </div>
            <div className="form-group issues-description-full">
              <label>Expected Result</label>
              <textarea name="expected" value={form.expected} onChange={handleChange} rows={2} disabled={!canEdit} />
            </div>
            <div className="form-group issues-description-full">
              <label>Actual Result</label>
              <textarea name="actual" value={form.actual} onChange={handleChange} rows={2} disabled={!canEdit} />
            </div>
            <div className="form-group issues-description-full">
              <label>Comments</label>
              <textarea name="comments" value={form.comments} onChange={handleChange} rows={2} disabled={!canEdit} />
            </div>
          </div>

          <div className="issues-form-actions">
            <button type="submit" className="btn-add-project" disabled={!canEdit || submitting}>
              {submitting ? "Updating..." : "Update Test Case"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditTestCase;

